import React, { useCallback } from "react";
import { makeStyles } from "@material-ui/styles";
import { ValidatorForm, TextValidator } from "react-material-ui-form-validator";
import { Modal, Card, Grid, Typography, TextField } from "@material-ui/core";
import {
	PageContent,
	FormActions,
	FormButton,
	FormSubmitButton,
	ScrollToTopOnMount,
	SelectInput,
} from "frame/components";
import { useRouter, sortByPropertyAlphabetically, isNotNullOrEmpty, useTerminology } from "utils";
import { DatePicker } from "@material-ui/pickers";
import { UploadForm } from "features/forms";
import PropTypes from "prop-types";

import {
	onTitleChanged,
	onDateChanged,
	onTypeChanged,
	onCustomerIdChanged,
	onSiteIdChanged,
	onStaffIdChanged,
	onNotesChanged,
	onFileChanged,
	onSave,
	onClose,
} from "../../actions";

const useStyles = makeStyles((theme) => ({
	root: {
		position: "absolute",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		outline: "none",
		boxShadow: theme.shadows[20],
		width: 700,
		maxHeight: "100%",
		overflowY: "auto",
		maxWidth: "100%"
	},
	actions: {
		justifyContent: "flex-end",
		marginBottom: theme.spacing(3)
	}
}));
const EditJobModal = ({
	job,
	customerList,
	staffList,
	siteList,
	isEditModalShowing,
	onTitleChanged,
	onDateChanged,
	onTypeChanged,
	onCustomerIdChanged,
	onSiteIdChanged,
	onStaffIdChanged,
	onNotesChanged,
	onFileChanged,
	onSave,
	onClose,
}) => {
	const classes = useStyles();
	const { history } = useRouter();
	const { customerLabel, siteLabel } = useTerminology();

	const isActive = (node) => String(node.status).toLowerCase() === "active";

	const customerOptions =
		isNotNullOrEmpty(customerList) &&
		customerList
			.filter(isActive)
			.map(({ id, customerName }) => ({
				label: customerName,
				value: id,
			}))
			.sort(sortByPropertyAlphabetically("label"));

	const siteOptions =
		isNotNullOrEmpty(siteList) &&
		siteList
			.filter(isActive)
			.filter(({ siteName }) => !(siteName || "").startsWith("Leave - "))
			.map(({ id, siteName }) => ({
				label: siteName,
				value: id,
			}))
			.sort(sortByPropertyAlphabetically("label"));

	const staffOptions =
		isNotNullOrEmpty(staffList) &&
		staffList
			.map(({ id, fullName, firstName, lastName }) => ({
				label: fullName || `${firstName} ${lastName}`.trim(),
				value: id,
			}))
			.filter((s) => s.label && s.value)
			.sort(sortByPropertyAlphabetically("label"));

	const typeOptions = [
		{ label: customerLabel, value: "customer" },
		{ label: siteLabel, value: "site" },
		{ label: "Staff", value: "staff" },
	];

	const onValueChange = useCallback((cb) => (event) => cb(event.target.value), []);
	const onSelectChange = useCallback((cb) => (value) => cb(value), []);
	const onSelectDateChange = useCallback(
		(cb) => (value) => cb(value ? value.utc().format() : null),
		[]
	);

	const onCancel = () => {
		onClose();
		// If the modal is being used on the /jobs add/edit page, preserve existing navigation behaviour.
		if (history.location.pathname && history.location.pathname.includes("/jobs")) {
			// no-op: let connected-react-router navigation be handled by existing flows
		}
	};

	return (
		<Modal onClose={onCancel} open={!!isEditModalShowing}>
			<Card className={classes.root}>
				<ValidatorForm
					onSubmit={(e) => {
						e.preventDefault();
						onSave();
					}}
				>
					<ScrollToTopOnMount />

					<PageContent padSides padBottom padTop>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<Typography gutterBottom variant="h3">
									Edit Photos / Notes
								</Typography>
							</Grid>

							<Grid item xs={12}>
								<TextValidator
									fullWidth
									data-cy="job_title"
									label="Title *"
									name="Title"
									value={job?.title || ""}
									variant="outlined"
									validators={["required"]}
									errorMessages={["This field is required"]}
									onChange={onValueChange(onTitleChanged)}
									inputProps={{ maxLength: 100 }}
								/>
							</Grid>

							<Grid item xs={12}>
								<DatePicker
									data-cy="job_date"
									className={classes.field}
									format="Do MMMM YYYY"
									fullWidth
									inputVariant="outlined"
									label="Date"
									value={job?.createdAt || null}
									variant="outlined"
									disableFuture={true}
									onChange={onSelectDateChange(onDateChanged)}
								/>
							</Grid>

							<Grid item xs={12}>
								<SelectInput
									id="type"
									data-cy="job_type"
									label="Type *"
									options={typeOptions}
									value={job?.type || ""}
									onChange={onSelectChange(onTypeChanged)}
								/>
							</Grid>

							{job?.type === "customer" && (
								<Grid item xs={12}>
									<SelectInput
										id="customer"
										data-cy="customer"
										label={`${customerLabel} Name *`}
										options={customerOptions}
										value={job?.customerId || ""}
										onChange={onSelectChange(onCustomerIdChanged)}
									/>
								</Grid>
							)}

							{job?.type === "site" && (
								<Grid item xs={12}>
									<SelectInput
										id="site"
										data-cy="site"
										label={`${siteLabel} Name *`}
										options={siteOptions}
										value={job?.siteId || ""}
										onChange={onSelectChange(onSiteIdChanged)}
									/>
								</Grid>
							)}

							{job?.type === "staff" && (
								<Grid item xs={12}>
									<SelectInput
										id="staff"
										data-cy="staff"
										label="Staff Name *"
										options={staffOptions}
										value={job?.staffId || ""}
										onChange={onSelectChange(onStaffIdChanged)}
									/>
								</Grid>
							)}

							<Grid item xs={12}>
								<TextField
									fullWidth
									id="job_notes"
									data-cy="job_notes"
									label="Notes"
									multiline
									rows="3"
									name="notes"
									onChange={onValueChange(onNotesChanged)}
									value={job?.notes || ""}
									variant="outlined"
								/>
							</Grid>

							<Grid item xs={12}>
								<Typography variant="h6">Attachments</Typography>
								<Typography variant="body2">
									Attach documents, photos and audio files.
								</Typography>
								<UploadForm
									ignoreDataUrl
									values={job || {}}
									onChange={onFileChanged}
								/>
							</Grid>
						</Grid>
					</PageContent>

					<FormActions className={classes.actions}>
						<FormSubmitButton>Save</FormSubmitButton>
						<FormButton onClick={onCancel}>Cancel</FormButton>
					</FormActions>
				</ValidatorForm>
			</Card>
		</Modal>
	);
};

EditJobModal.propTypes = {
	job: PropTypes.object,
	customerList: PropTypes.array.isRequired,
	siteList: PropTypes.array.isRequired,
	staffList: PropTypes.array.isRequired,
	isEditModalShowing: PropTypes.bool.isRequired,
	onTitleChanged: PropTypes.func.isRequired,
	onDateChanged: PropTypes.func.isRequired,
	onTypeChanged: PropTypes.func.isRequired,
	onCustomerIdChanged: PropTypes.func.isRequired,
	onSiteIdChanged: PropTypes.func.isRequired,
	onStaffIdChanged: PropTypes.func.isRequired,
	onNotesChanged: PropTypes.func.isRequired,
	onFileChanged: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default EditJobModal;
